import React, { useState } from 'react';
import { Heart, MessageCircle, Send, Bookmark, ChevronLeft, ChevronRight, MoreHorizontal } from 'lucide-react';

/* ─── Instagram Post View ─── */
const InstagramPostView = ({ slides, allSlides, caption, hashtags, theme, themes, renderSlide }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [liked, setLiked] = useState(false);
  const [saved, setSaved] = useState(false);
  const totalSlides = allSlides.length;

  const handlePrev = () => setCurrentSlide(prev => Math.max(0, prev - 1));
  const handleNext = () => setCurrentSlide(prev => Math.min(totalSlides - 1, prev + 1));

  return (
    <div data-testid="instagram-post-view" className="bg-white rounded-2xl border border-stone-200 overflow-hidden max-w-[470px] mx-auto shadow-lg">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-orange-400 flex items-center justify-center">
            <span className="text-white text-xs font-bold">S</span>
          </div>
          <div>
            <p className="text-sm font-sans font-semibold text-stone-900 leading-tight">yourbrand</p>
            <p className="text-[11px] font-sans text-stone-500 leading-tight">Your Location</p>
          </div>
        </div>
        <MoreHorizontal className="w-5 h-5 text-stone-600" />
      </div>

      {/* Carousel */}
      <div className="relative aspect-square bg-stone-100 overflow-hidden group">
        {/* Slide content */}
        <div className="w-full h-full">
          {renderSlide(currentSlide)}
        </div>

        {/* Navigation arrows */}
        {currentSlide > 0 && (
          <button
            onClick={handlePrev}
            className="absolute left-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-white/90 shadow-md flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <ChevronLeft className="w-4 h-4 text-stone-700" />
          </button>
        )}
        {currentSlide < totalSlides - 1 && (
          <button
            onClick={handleNext}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-white/90 shadow-md flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <ChevronRight className="w-4 h-4 text-stone-700" />
          </button>
        )}

        {/* Slide counter */}
        <div className="absolute top-3 right-3 bg-stone-900/70 text-white text-xs font-sans font-medium px-2.5 py-1 rounded-full">
          {currentSlide + 1}/{totalSlides}
        </div>
      </div>

      {/* Action buttons */}
      <div className="px-4 pt-3 pb-1">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-4">
            <button onClick={() => setLiked(!liked)} className="transition-transform active:scale-125">
              <Heart className={`w-6 h-6 ${liked ? 'text-red-500 fill-red-500' : 'text-stone-800'}`} strokeWidth={1.5} />
            </button>
            <MessageCircle className="w-6 h-6 text-stone-800 -scale-x-100" strokeWidth={1.5} />
            <Send className="w-6 h-6 text-stone-800" strokeWidth={1.5} />
          </div>
          {/* Dot indicators */}
          <div className="absolute left-1/2 -translate-x-1/2 flex items-center gap-1">
            {allSlides.map((_, i) => (
              <div
                key={i}
                className="rounded-full transition-all duration-200"
                style={{
                  width: i === currentSlide ? '6px' : '4px',
                  height: i === currentSlide ? '6px' : '4px',
                  backgroundColor: i === currentSlide ? '#3B82F6' : '#D1D5DB'
                }}
              />
            ))}
          </div>
          <button onClick={() => setSaved(!saved)}>
            <Bookmark className={`w-6 h-6 ${saved ? 'text-stone-900 fill-stone-900' : 'text-stone-800'}`} strokeWidth={1.5} />
          </button>
        </div>

        {/* Likes */}
        <p className="text-sm font-sans font-semibold text-stone-900 mb-1">
          {liked ? '2,487' : '2,486'} likes
        </p>

        {/* Caption */}
        {caption && (
          <div className="mb-2">
            <p className="text-sm font-sans text-stone-800 leading-relaxed">
              <span className="font-semibold">yourbrand</span>{' '}
              <span className="whitespace-pre-line">{caption.substring(0, 140)}{caption.length > 140 ? '...' : ''}</span>
            </p>
            {caption.length > 140 && (
              <button className="text-sm font-sans text-stone-400">more</button>
            )}
          </div>
        )}

        {/* Hashtags preview */}
        {hashtags && (
          <p className="text-sm font-sans text-blue-400 mb-2 truncate">{hashtags.substring(0, 80)}...</p>
        )}

        {/* Comments */}
        <p className="text-sm font-sans text-stone-400 mb-1">View all 48 comments</p>
        <p className="text-[11px] font-sans text-stone-400 uppercase tracking-wide mb-3">2 hours ago</p>
      </div>
    </div>
  );
};

/* ─── Instagram Feed View ─── */
const InstagramFeedView = ({ allSlides, theme, themes, renderSlide }) => {
  const t = themes[theme] || themes['modern-minimal'];

  // Generate placeholder posts for the grid
  const placeholderColors = ['#F5F0EB', '#E8E4DF', '#F0EDE8', '#E5E0DB', '#ECE8E3', '#DDD8D3', '#F2EEEA', '#E0DBD6'];

  return (
    <div data-testid="instagram-feed-view" className="max-w-[470px] mx-auto">
      {/* Profile header */}
      <div className="bg-white rounded-t-2xl border border-b-0 border-stone-200 p-5">
        <div className="flex items-center gap-5">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary to-orange-400 flex items-center justify-center flex-shrink-0 ring-2 ring-offset-2 ring-primary/30">
            <span className="text-white text-2xl font-bold">S</span>
          </div>
          <div className="flex-1">
            <p className="text-base font-sans font-bold text-stone-900">yourbrand</p>
            <p className="text-sm font-sans text-stone-500 mb-2">Your Brand Name</p>
            <div className="flex gap-6">
              <div className="text-center">
                <p className="text-sm font-sans font-bold text-stone-900">42</p>
                <p className="text-[11px] font-sans text-stone-500">posts</p>
              </div>
              <div className="text-center">
                <p className="text-sm font-sans font-bold text-stone-900">3.2k</p>
                <p className="text-[11px] font-sans text-stone-500">followers</p>
              </div>
              <div className="text-center">
                <p className="text-sm font-sans font-bold text-stone-900">890</p>
                <p className="text-[11px] font-sans text-stone-500">following</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Grid tabs */}
      <div className="bg-white border-x border-stone-200 flex">
        <button className="flex-1 py-2.5 border-b-2 border-stone-900">
          <svg className="w-5 h-5 mx-auto text-stone-900" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25a2.25 2.25 0 01-2.25-2.25v-2.25z" />
          </svg>
        </button>
        <button className="flex-1 py-2.5 border-b border-stone-200">
          <svg className="w-5 h-5 mx-auto text-stone-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3.375 19.5h17.25m-17.25 0a1.125 1.125 0 01-1.125-1.125M3.375 19.5h1.5C5.496 19.5 6 18.996 6 18.375m-3.75 0V5.625m0 12.75v-1.5c0-.621.504-1.125 1.125-1.125m18.375 2.625V5.625m0 12.75c0 .621-.504 1.125-1.125 1.125m1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125m0 3.75h-1.5A1.125 1.125 0 0118 18.375M20.625 4.5H3.375m17.25 0c.621 0 1.125.504 1.125 1.125M20.625 4.5h-1.5C18.504 4.5 18 5.004 18 5.625m3.75 0v1.5c0 .621-.504 1.125-1.125 1.125M3.375 4.5c-.621 0-1.125.504-1.125 1.125M3.375 4.5h1.5C5.496 4.5 6 5.004 6 5.625m-3.75 0v1.5c0 .621.504 1.125 1.125 1.125m0 0h1.5m-1.5 0c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125m1.5-3.75C5.496 8.25 6 7.746 6 7.125v-1.5M4.875 8.25C5.496 8.25 6 8.754 6 9.375v1.5c0 .621-.504 1.125-1.125 1.125m1.5 0h1.5m-1.5 0c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125M6 12h.008v.008H6V12z" />
          </svg>
        </button>
      </div>

      {/* Post grid */}
      <div className="bg-white rounded-b-2xl border border-t-0 border-stone-200 overflow-hidden">
        <div className="grid grid-cols-3 gap-px bg-stone-200">
          {/* First cell = your carousel (cover slide) */}
          <div className="relative aspect-square overflow-hidden bg-white">
            <div className="w-full h-full" style={{ transform: 'scale(1)', transformOrigin: 'center center' }}>
              {renderSlide(0)}
            </div>
            {/* Multi-slide indicator */}
            <div className="absolute top-2 right-2">
              <svg className="w-4 h-4 text-white drop-shadow-md" fill="currentColor" viewBox="0 0 24 24">
                <path d="M4 6h12a2 2 0 012 2v12a2 2 0 01-2 2H4a2 2 0 01-2-2V8a2 2 0 012-2zm14-4H8v2h10a2 2 0 012 2v10h2V4a2 2 0 00-2-2z"/>
              </svg>
            </div>
          </div>

          {/* Placeholder posts */}
          {placeholderColors.map((color, i) => (
            <div
              key={i}
              className="aspect-square"
              style={{ backgroundColor: color }}
            >
              {/* Subtle placeholder pattern */}
              <div className="w-full h-full flex items-center justify-center opacity-20">
                <div className="w-8 h-8 rounded" style={{ backgroundColor: '#00000015' }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ─── Main Export ─── */
export { InstagramPostView, InstagramFeedView };
